import * as React from "react";

import { type ConditionOption } from "@/shared/components/filters/FilterDefinition";
import { optionConditionsOf } from "@/shared/components/filters/filterToCondition";
import { useAuditIdentityOptions } from "@/shared/components/principal";
import { type FilterDefinition } from "@/shared/components/ResourceView";
import {
  hydrateFilterDefinitions,
  SESSION_CORE_FILTER_FIELDS,
} from "@/shared/components/ResourceView/Filters/catalog";

export {
  sessionDatasetIdFilter,
  sessionDeviceIdFilter,
} from "@/shared/components/ResourceView/Filters/catalog";

/**
 * The Status filter's "In progress": an in-progress session the Status column
 * labels "In progress", which leaves out those whose cell is blank (no
 * completion policy, never marked complete; see `tracksCompletion`).
 */
const IN_PROGRESS_OPTION: ConditionOption = {
  label: "In progress",
  value: "in_progress",
  condition: {
    operator: "AND",
    conditions: [
      { field: "status", comparator: "EQUALS", value: "in_progress" },
      {
        operator: "OR",
        conditions: [
          { field: "completed_at", comparator: "IS_NOT_NULL", value: "" },
          { field: "completion_policy", comparator: "IS_NOT_NULL", value: "" },
        ],
      },
    ],
  },
};

/**
 * Rows for a session's completion and ingestion, which `SessionsList` adds to
 * a mount's filters where session ingestion is released. No Status option
 * tells "Fully ingested" apart, since RoboQL cannot compare `ingested_at` with
 * `completed_at`.
 */
export const SESSION_INGESTION_FILTERS: FilterDefinition[] = [
  {
    type: "enum",
    field: "status",
    name: "Status",
    options: [IN_PROGRESS_OPTION, { label: "Complete", value: "complete" }],
    nullable: false,
  },
  { type: "date", field: "ingested_at", name: "Fully ingested at" },
];

/** What the Status filter's "In progress" option stands for. */
export const SESSION_OPTION_CONDITIONS = optionConditionsOf(
  SESSION_INGESTION_FILTERS,
);

/**
 * Filterable Sessions fields common to every Sessions list mount. Mounts
 * compose their picker by spreading the array this hook returns and appending
 * the cross-aggregate id rows their scope admits: `sessionDatasetIdFilter`,
 * `sessionDeviceIdFilter`, or both.
 *
 * A hook (not a static const) because the `created_by` / `modified_by`
 * identity filters need org-scoped directories — see
 * {@link useAuditIdentityOptions}. The rows themselves live in the shared
 * filter catalog, where the chat's View tools read them too.
 */
export function useCoreSessionFilters(
  orgId: string | undefined,
): FilterDefinition[] {
  const identityOptions = useAuditIdentityOptions(orgId);

  return React.useMemo(
    () =>
      hydrateFilterDefinitions(SESSION_CORE_FILTER_FIELDS, { identityOptions }),
    [identityOptions],
  );
}
